import { Paths } from "@app/routes";
import { useTranslation } from "@atlas/sdk";
import {
  loadRemote,
  registerRemotes,
} from "@module-federation/enhanced/runtime";
import {
  loadClassicWebpackRemote,
  resolveBridgeFromModule,
  resolveRemoteComponent,
} from "@shared/federation/loadClassicWebpackRemote";
import { getRemoteEntryUrl } from "@shared/federation/remoteUrl";
import type {
  IBridgeInstance,
  IFederatedRemoteConfig,
} from "@shared/federation/types";
import type React from "react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { MicroFrontendLoadingFallback } from "./MicroFrontendLoadingFallback";

interface IDynamicMicroFrontendProps {
  route: IFederatedRemoteConfig;
  fallback?: React.ReactNode;
}

const mfeHostClassName =
  "flex min-h-0 min-w-0 flex-1 h-full w-full max-w-full [&>*]:min-h-0 [&>*]:min-w-0 [&>*]:h-full [&>*]:w-full [&>*]:max-w-full";

function isBridgeModule(module: unknown): boolean {
  return resolveBridgeFromModule(module, { isEmbedded: true }) !== null;
}

export function DynamicMicroFrontend({
  route,
  fallback,
}: IDynamicMicroFrontendProps) {
  const { t } = useTranslation();
  const [Component, setComponent] = useState<React.ComponentType<{
    isEmbedded?: boolean;
    basename?: string;
  }> | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);

  const containerRef = useRef<HTMLDivElement>(null);
  const bridgeRef = useRef<IBridgeInstance | null>(null);

  useEffect(() => {
    let isMounted = true;

    async function loadEnhancedRemote(): Promise<unknown> {
      const remoteUrl = getRemoteEntryUrl(route.remoteEntryPath);

      try {
        await registerRemotes([
          {
            name: route.remoteName,
            entry: remoteUrl,
          },
        ]);
      } catch (registerError) {
        if (!(registerError as Error).message?.includes("already registered")) {
          console.warn(
            t("hub.mfe.register_failed", {
              label: route.label,
              defaultValue: "Failed to register remote: {{label}}",
            }),
            registerError,
          );
        }
      }

      let module: unknown = null;

      for (const expose of route.exposes) {
        const remotePath = `${route.remoteName}/${expose}`;

        if (expose === "Bridge") {
          try {
            module = await loadRemote(remotePath);
            if (isBridgeModule(module)) {
              break;
            }
          } catch (loadError) {
            console.warn(`Failed to load ${remotePath}`, loadError);
          }
          continue;
        }

        try {
          module = await loadRemote(remotePath);
          if (
            resolveRemoteComponent(module) ||
            resolveBridgeFromModule(module, { isEmbedded: true })
          ) {
            break;
          }
        } catch (loadError) {
          console.warn(`Failed to load ${remotePath}`, loadError);
        }
      }

      return module;
    }

    async function loadClassicRemote(): Promise<unknown> {
      const remoteUrl = getRemoteEntryUrl(route.remoteEntryPath);
      const errors: Error[] = [];

      for (const expose of route.exposes) {
        try {
          const module = await loadClassicWebpackRemote(
            remoteUrl,
            route.remoteName,
            expose,
          );
          if (module !== null && module !== undefined) {
            return module;
          }
        } catch (loadError) {
          const error =
            loadError instanceof Error
              ? loadError
              : new Error(String(loadError));
          console.warn(
            `Failed to load classic remote ${route.remoteName}/${expose}`,
            error,
          );
          errors.push(error);
        }
      }

      throw (
        errors.at(-1) ??
        new Error(
          t("hub.mfe.module_not_found", {
            remote: route.remoteName,
            defaultValue: "Remote module not found for {{remote}}",
          }),
        )
      );
    }

    async function loadComponent() {
      try {
        setLoading(true);
        setError(null);
        setComponent(null);
        bridgeRef.current = null;

        const module =
          route.remoteType === "classic"
            ? await loadClassicRemote()
            : await loadEnhancedRemote();

        if (module === null || module === undefined) {
          throw new Error(
            t("hub.mfe.module_not_found", {
              remote: route.remoteName,
              defaultValue: "Remote module not found for {{remote}}",
            }),
          );
        }

        const bridgeInstance = resolveBridgeFromModule(module, {
          isEmbedded: true,
          basename: route.url,
        });
        if (bridgeInstance) {
          if (isMounted) {
            bridgeRef.current = bridgeInstance;
            setLoading(false);
          }
          return;
        }

        const remoteComponent = resolveRemoteComponent(module);
        if (!remoteComponent) {
          throw new Error(
            t("hub.mfe.export_not_found", {
              remote: route.remoteName,
              defaultValue: "Default export not found for {{remote}}",
            }),
          );
        }

        if (isMounted) {
          setComponent(() => remoteComponent);
          setLoading(false);
        }
      } catch (err) {
        console.error(
          t("hub.mfe.load_failed", {
            label: route.label,
            defaultValue: "Failed to load {{label}}",
          }),
          err,
        );
        if (isMounted) {
          setError(err instanceof Error ? err : new Error(String(err)));
          setLoading(false);
        }
      }
    }

    loadComponent();

    return () => {
      isMounted = false;
      if (bridgeRef.current && containerRef.current) {
        bridgeRef.current.destroy({ dom: containerRef.current });
        bridgeRef.current = null;
      }
    };
  }, [route, t]);

  useLayoutEffect(() => {
    if (loading || error || !bridgeRef.current || !containerRef.current) {
      return;
    }

    const container = containerRef.current;
    const bridge = bridgeRef.current;
    let cancelled = false;

    const renderBridge = () => {
      if (cancelled) {
        return;
      }

      bridge
        .render({
          dom: container,
          props: {
            isEmbedded: true,
            basename: route.url,
            width: "100%",
            height: "100%",
          },
        })
        .catch((e) => {
          console.error("Failed to render federated bridge", e);
          setError(e instanceof Error ? e : new Error(String(e)));
        });
    };

    // Defer until the host container has layout dimensions.
    const scheduleRender = () => {
      if (container.clientWidth > 0) {
        renderBridge();
        return;
      }
      requestAnimationFrame(() => {
        if (!cancelled) {
          renderBridge();
        }
      });
    };

    scheduleRender();

    return () => {
      cancelled = true;
    };
  }, [loading, error, route.url]);

  if (loading) {
    return fallback ?? <MicroFrontendLoadingFallback label={route.label} />;
  }

  if (error) {
    throw error;
  }

  if (Component) {
    return (
      <div className={mfeHostClassName}>
        <Component isEmbedded={true} basename={route.url} />
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={mfeHostClassName}
      style={{ width: "100%", minWidth: 0, flex: 1 }}
    />
  );
}
